import { Graphics, Text, TextStyle, Container } from 'pixi.js';

export interface ButtonStyle {
  backgroundColor?: number;
  hoverColor?: number;
  pressedColor?: number;
  disabledColor?: number;
  borderColor?: number;
  borderWidth?: number;
  cornerRadius?: number;
  fontSize?: number;
  fontColor?: number;
  glowColor?: number;
  glowSize?: number;
  holdToRepeat?: boolean;
}

export class Button extends Container {
  private background: Graphics;
  private buttonText: Text;
  private style: ButtonStyle;
  private isEnabled: boolean = true;
  private isHovered: boolean = false;
  private isPressed: boolean = false;
  private glowGraphics: Graphics;
  private pulseAnimation: number = 0;
  private callback?: () => void;
  private buttonWidth: number;
  private buttonHeight: number;
  private holdTimer?: ReturnType<typeof setInterval>;
  private holdDelay: number = 300;
  private holdInterval: number = 100;
  private holdToRepeat: boolean;
  
  constructor(
    text: string,
    width: number,
    height: number,
    style: ButtonStyle = {}
  ) {
    super();
    
    this.buttonWidth = width;
    this.buttonHeight = height;
    this.holdToRepeat = style.holdToRepeat ?? false;
    
    this.style = {
      backgroundColor: style.backgroundColor ?? 0xFF6B35,
      hoverColor: style.hoverColor ?? 0xFF8C5A,
      pressedColor: style.pressedColor ?? 0xE55A2B,
      disabledColor: style.disabledColor ?? 0x888888,
      borderColor: style.borderColor ?? 0xFFFFFF,
      borderWidth: style.borderWidth ?? 3,
      cornerRadius: style.cornerRadius ?? 12,
      fontSize: style.fontSize ?? 24,
      fontColor: style.fontColor ?? 0xFFFFFF,
      glowColor: style.glowColor ?? 0xFFD700,
      glowSize: style.glowSize ?? 20,
      ...style
    };
    
    this.glowGraphics = new Graphics();
    this.addChild(this.glowGraphics);
    
    this.background = new Graphics();
    this.addChild(this.background);
    
    const textStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: this.style.fontSize,
      fontWeight: 'bold',
      fill: this.style.fontColor,
      stroke: { color: 0x000000, width: 2 },
      dropShadow: {
        color: 0x000000,
        blur: 4,
        distance: 2
      }
    });
    
    this.buttonText = new Text({ text, style: textStyle });
    this.buttonText.anchor.set(0.5);
    this.addChild(this.buttonText);
    
    this.draw();
    this.setupInteractivity();
  }
  
  private draw(): void {
    const width = this.buttonWidth;
    const height = this.buttonHeight;
    
    this.drawBackground(width, height);
    this.drawGlow(width, height);
  }
  
  private drawBackground(width: number, height: number): void {
    this.background.clear();
    
    let color = this.style.backgroundColor!;
    
    if (!this.isEnabled) {
      color = this.style.disabledColor!;
    } else if (this.isPressed) {
      color = this.style.pressedColor!;
    } else if (this.isHovered) {
      color = this.style.hoverColor!;
    }
    
    this.background.roundRect(-width/2, -height/2, width, height, this.style.cornerRadius!);
    this.background.fill({ color });
    
    if (this.style.borderWidth! > 0) {
      this.background.stroke({ 
        color: this.style.borderColor!, 
        width: this.style.borderWidth! 
      });
    }
  }
  
  private drawGlow(width: number, height: number): void {
    this.glowGraphics.clear();
    
    if (this.isEnabled && this.isHovered) {
      this.pulseAnimation += 0.05;
      const pulse = Math.sin(this.pulseAnimation) * 0.3 + 0.7;
      const alpha = pulse * 0.3;
      
      for (let i = 3; i >= 0; i--) {
        this.glowGraphics.roundRect(
          -width/2 - i * 3,
          -height/2 - i * 3,
          width + i * 6,
          height + i * 6,
          this.style.cornerRadius! + i * 2
        );
        this.glowGraphics.fill({ 
          color: this.style.glowColor!, 
          alpha: alpha / (i + 1) 
        });
      }
    }
  }
  
  private setupInteractivity(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';
    
    this.on('pointerover', () => {
      if (this.isEnabled) {
        this.isHovered = true;
        this.draw();
      }
    });
    
    this.on('pointerout', () => {
      this.isHovered = false;
      this.isPressed = false;
      this.draw();
      if (this.holdTimer) {
        clearTimeout(this.holdTimer);
        clearInterval(this.holdTimer);
        this.holdTimer = undefined;
      }
    });
    
    this.on('pointerdown', () => {
      if (this.isEnabled) {
        this.isPressed = true;
        this.draw();
        
        // Fire once immediately
        if (this.callback) {
          this.callback();
        }
        
        // Only setup hold-to-repeat if enabled
        if (this.holdToRepeat) {
          // Clear any existing timer
          if (this.holdTimer) {
            clearInterval(this.holdTimer);
          }
          
          // Start hold-to-repeat
          this.holdTimer = setTimeout(() => {
            if (this.isPressed && this.callback) {
              // Start rapid firing after initial delay
              this.holdTimer = setInterval(() => {
                if (this.isPressed && this.callback) {
                  this.callback();
                }
              }, this.holdInterval);
            }
          }, this.holdDelay);
        }
      }
    });
    
    this.on('pointerup', () => {
      if (this.isEnabled && this.isPressed) {
        this.isPressed = false;
        this.draw();
        if (this.holdTimer) {
          clearTimeout(this.holdTimer);
          clearInterval(this.holdTimer);
          this.holdTimer = undefined;
        }
      }
    });
  }
  
  public setCallback(callback: () => void): void {
    this.callback = callback;
  }
  
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.eventMode = enabled ? 'static' : 'none';
    this.alpha = enabled ? 1 : 0.5;
    this.draw();
  }
  
  public setText(text: string): void {
    this.buttonText.text = text;
  }
  
  public update(delta: number): void {
    // Only update if hovered - no continuous redraw needed
    // Glow is drawn once in draw() method
  }
  
  public destroy(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      clearInterval(this.holdTimer);
      this.holdTimer = undefined;
    }
    super.destroy();
  }
}